import React, { useCallback, useEffect, useMemo, useState } from "react";

import monitorApi from "../../api/monitorApi";
import Header from "../../components/Header/Header";
import ShutdownAddNav from "../../components/ShutdownAddNav/ShutdownAddNav";
import {
  IssuedTokenResponseType,
  UploadTokenListItemType,
} from "./WhiteListUploadTokensPageTypes";

const DEFAULT_MAX_USES_PER_24H = 5;

function formatTs(value: string | null): string {
  if (!value) {
    return "-";
  }
  const d = new Date(value);
  if (Number.isNaN(d.getTime())) {
    return value;
  }
  return d.toLocaleString();
}

function statusLabel(item: UploadTokenListItemType): string {
  if (item.is_used) {
    return "used";
  }
  if (item.is_revoked) {
    return "revoked";
  }
  if (item.is_expired) {
    return "expired";
  }
  if (item.is_rate_limited) {
    return "rate-limited";
  }
  if (item.is_reusable) {
    return "reusable";
  }
  return "unused";
}

function kindLabel(item: UploadTokenListItemType): string {
  return item.is_reusable ? "reusable" : "one-time";
}

function usesLabel(item: UploadTokenListItemType): string {
  if (!item.is_reusable) {
    return "-";
  }
  const used = item.uses_last_24h ?? 0;
  const max = item.max_uses_per_24h ?? DEFAULT_MAX_USES_PER_24H;
  return `${used} / ${max}`;
}

function parseMaxUses(raw: string): number {
  const parsed = Number.parseInt(raw, 10);
  if (!Number.isFinite(parsed) || parsed < 1) {
    return DEFAULT_MAX_USES_PER_24H;
  }
  return parsed;
}

function issuedAtSortValue(value: string | null): number {
  const ms = Date.parse(value ?? "");
  return Number.isNaN(ms) ? 0 : ms;
}

function sortTokensNewestFirst(
  items: UploadTokenListItemType[],
): UploadTokenListItemType[] {
  return [...items].sort((a, b) => {
    const byIssued =
      issuedAtSortValue(b.issued_at) - issuedAtSortValue(a.issued_at);
    if (byIssued !== 0) {
      return byIssued;
    }
    return b.id - a.id;
  });
}

function WhiteListUploadTokensPage() {
  const [tokens, setTokens] = useState<UploadTokenListItemType[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [issuing, setIssuing] = useState(false);
  const [issued, setIssued] = useState<IssuedTokenResponseType | null>(null);
  const [copyOk, setCopyOk] = useState(false);
  const [expiresAt, setExpiresAt] = useState("");
  const [maxUsesPer24h, setMaxUsesPer24h] = useState(
    String(DEFAULT_MAX_USES_PER_24H),
  );
  const [revokeTarget, setRevokeTarget] =
    useState<UploadTokenListItemType | null>(null);
  const [revoking, setRevoking] = useState(false);

  const loadTokens = useCallback(() => {
    setLoading(true);
    setError(null);
    monitorApi
      .listWhiteListUploadTokens()
      .then((res) => {
        if (res.data?.status === "ok" && Array.isArray(res.data.results)) {
          setTokens(res.data.results);
        } else {
          setError("Failed to load tokens");
        }
      })
      .catch(() => {
        setError("Failed to load tokens");
      })
      .finally(() => {
        setLoading(false);
      });
  }, []);

  useEffect(() => {
    loadTokens();
  }, [loadTokens]);

  const sortedTokens = useMemo(() => sortTokensNewestFirst(tokens), [tokens]);

  const handleIssue = () => {
    setIssuing(true);
    setError(null);
    setCopyOk(false);
    const payload = {
      expires_at: expiresAt.trim() ? expiresAt.trim() : null,
      max_uses_per_24h: parseMaxUses(maxUsesPer24h),
    };
    monitorApi
      .issueWhiteListUploadToken(payload)
      .then((res) => {
        if (res.data?.token) {
          setIssued(res.data);
          loadTokens();
        } else {
          setError("Issue failed");
        }
      })
      .catch(() => {
        setError("Issue failed");
      })
      .finally(() => {
        setIssuing(false);
      });
  };

  const handleCopy = () => {
    void navigator.clipboard
      .writeText(issued!.token)
      .then(() => setCopyOk(true));
  };

  const confirmRevoke = () => {
    setRevoking(true);
    monitorApi
      .revokeWhiteListUploadToken(revokeTarget!.id)
      .then(() => {
        setRevokeTarget(null);
        loadTokens();
      })
      .catch(() => {
        setError("Revoke failed");
      })
      .finally(() => {
        setRevoking(false);
      });
  };

  return (
    <main className="w-full">
      <Header />
      <ShutdownAddNav />
      <div
        data-testid="white-list-upload-tokens-page"
        className="p-[1rem] md:p-[2rem] w-auto max-w-[90rem] mx-auto"
      >
        <h1 className="text-2xl font-semibold mb-2">One-time upload tokens</h1>
        <p className="text-sm text-gray-600 mb-4">
          Issue a token for a volunteer. They paste the same value as zip
          password and X-Web-Token. Plaintext is shown once after issue. Leave
          expiry empty for one successful upload. Set a date for repeated
          uploads until that date, limited by uses per 24 hours.
        </p>

        <div className="flex flex-col gap-4 mb-4">
          <div className="flex flex-col md:flex-row md:items-end gap-4">
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-800">Expires at</span>
              <input
                type="date"
                className="p-2 bg-slate-100 rounded border border-slate-200"
                value={expiresAt}
                onChange={(e) => setExpiresAt(e.target.value)}
                data-testid="expires-at-input"
              />
              <span className="text-xs text-gray-500">
                Optional. Empty = one-time use.
              </span>
            </label>
            <label className="flex flex-col gap-1 text-sm">
              <span className="font-medium text-gray-800">
                Uses per 24 hours
              </span>
              <input
                type="number"
                min={1}
                className="p-2 bg-slate-100 rounded border border-slate-200 w-28"
                value={maxUsesPer24h}
                onChange={(e) => setMaxUsesPer24h(e.target.value)}
                data-testid="max-uses-per-24h-input"
              />
              <span className="text-xs text-gray-500">
                Applies only when expiry is set. Default 5.
              </span>
            </label>
          </div>
          <button
            type="button"
            data-testid="issue-upload-token"
            className="px-4 py-2 rounded-lg bg-orange-100 hover:bg-orange-200 text-orange-900 font-medium self-start"
            onClick={handleIssue}
            disabled={issuing}
          >
            {issuing
              ? "Issuing..."
              : expiresAt.trim()
                ? "Issue token"
                : "Issue one-time token"}
          </button>
        </div>

        {error ? (
          <p data-testid="upload-tokens-error" className="mt-3 text-red-700">
            {error}
          </p>
        ) : null}

        {issued ? (
          <div
            data-testid="issued-token-panel"
            className="mt-4 p-4 border border-orange-200 bg-orange-50"
          >
            <p className="font-medium mb-2">
              Copy this token now (shown once):
            </p>
            <code
              data-testid="issued-token-value"
              className="block break-all text-sm mb-3"
            >
              {issued.token}
            </code>
            <button
              type="button"
              data-testid="copy-issued-token"
              className="px-3 py-1 rounded bg-white border border-orange-300"
              onClick={handleCopy}
            >
              {copyOk ? "Copied" : "Copy"}
            </button>
            {issued.is_reusable ? (
              <p
                className="text-sm mt-3 text-gray-700"
                data-testid="issued-token-instructions"
              >
                Volunteer instructions: paste this 32-character token when the
                checker asks for an upload token. Use hop-first upload. The
                token can be reused until {formatTs(issued.expires_at)}, up to{" "}
                {issued.max_uses_per_24h ?? DEFAULT_MAX_USES_PER_24H} successful
                uploads per 24 hours.
              </p>
            ) : (
              <p
                className="text-sm mt-3 text-gray-700"
                data-testid="issued-token-instructions"
              >
                Volunteer instructions: paste this 32-character token when the
                checker asks for an upload token. Use hop-first upload. The
                token expires in 7 days and works for one successful upload.
              </p>
            )}
            <p className="text-xs mt-2 text-gray-500">
              Expires: {formatTs(issued.expires_at)}
            </p>
          </div>
        ) : null}

        <h2 className="text-xl font-semibold mt-8 mb-3">Issued tokens</h2>
        {loading ? (
          <p data-testid="upload-tokens-loading">Loading...</p>
        ) : (
          <div className="overflow-x-auto">
            <table
              data-testid="upload-tokens-table"
              className="min-w-full text-left text-sm border-collapse"
            >
              <thead>
                <tr className="border-b">
                  <th className="py-2 pr-3">Prefix</th>
                  <th className="py-2 pr-3">Kind</th>
                  <th className="py-2 pr-3">Issued</th>
                  <th className="py-2 pr-3">Used</th>
                  <th className="py-2 pr-3">Expires</th>
                  <th className="py-2 pr-3">Uses / 24h</th>
                  <th className="py-2 pr-3">Status</th>
                  <th className="py-2 pr-3">Actions</th>
                </tr>
              </thead>
              <tbody>
                {sortedTokens.map((item) => {
                  const canRevoke =
                    !item.is_used && !item.is_revoked && !item.is_expired;
                  return (
                    <tr
                      key={item.id}
                      className="border-b"
                      data-testid={`token-row-${item.id}`}
                    >
                      <td className="py-2 pr-3 font-mono">{item.prefix}</td>
                      <td className="py-2 pr-3">{kindLabel(item)}</td>
                      <td className="py-2 pr-3">{formatTs(item.issued_at)}</td>
                      <td className="py-2 pr-3">
                        {item.used_at ? formatTs(item.used_at) : "unused"}
                      </td>
                      <td className="py-2 pr-3">{formatTs(item.expires_at)}</td>
                      <td
                        className="py-2 pr-3"
                        data-testid={`token-uses-${item.id}`}
                      >
                        {usesLabel(item)}
                      </td>
                      <td className="py-2 pr-3">{statusLabel(item)}</td>
                      <td className="py-2 pr-3">
                        {canRevoke ? (
                          <button
                            type="button"
                            data-testid={`revoke-token-${item.id}`}
                            className="px-2 py-1 rounded bg-red-100 text-red-800"
                            onClick={() => setRevokeTarget(item)}
                          >
                            Revoke
                          </button>
                        ) : (
                          "-"
                        )}
                      </td>
                    </tr>
                  );
                })}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {revokeTarget ? (
        <div
          role="dialog"
          aria-label="Confirm revoke"
          className="fixed inset-0 z-50 flex items-center justify-center bg-black/30 backdrop-blur-sm"
          onClick={() => setRevokeTarget(null)}
        >
          <div
            className="bg-white p-6 rounded-lg shadow-lg max-w-md w-full mx-4"
            onClick={(e) => e.stopPropagation()}
          >
            <h3 className="text-lg font-semibold mb-2">Revoke token?</h3>
            <p className="text-sm mb-4">
              Prefix {revokeTarget.prefix} will no longer accept uploads.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                type="button"
                data-testid="revoke-cancel"
                className="px-3 py-1 rounded border"
                onClick={() => setRevokeTarget(null)}
              >
                Cancel
              </button>
              <button
                type="button"
                data-testid="revoke-confirm"
                className="px-3 py-1 rounded bg-red-600 text-white"
                onClick={confirmRevoke}
                disabled={revoking}
              >
                {revoking ? "Revoking..." : "Confirm"}
              </button>
            </div>
          </div>
        </div>
      ) : null}
    </main>
  );
}

export default WhiteListUploadTokensPage;
